import React, { useState } from 'react';
import { CareerLevel, ConsultantNode } from '../../types/career';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Slider } from '../ui/Slider';
import { calculateCareerGap } from '../../math/qualification';
import { getLevelTitle } from '../../math/careerRules';
import { formatAE, formatCurrencyUAH } from '../../utils/formatters';
import {
  Target,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Sparkles,
  Award,
  GitBranch,
} from 'lucide-react';

export interface GoalTrackerProps {
  rootNode: ConsultantNode;
  currentIncomeUAH: number;
  className?: string;
}

const INCOME_PRESETS = [
  { label: '50 000 ₴', value: 50_000 },
  { label: '100 000 ₴', value: 100_000 },
  { label: '200 000 ₴', value: 200_000 },
  { label: '500 000 ₴', value: 500_000 },
  { label: '1 000 000 ₴', value: 1_000_000 },
];

export const GoalTracker: React.FC<GoalTrackerProps> = ({
  rootNode,
  currentIncomeUAH,
  className = '',
}) => {
  const [targetIncomeUAH, setTargetIncomeUAH] = useState<number>(200_000);
  const [selectedTargetLevel, setSelectedTargetLevel] = useState<CareerLevel | null>(null);

  const directBranchLevels = rootNode.children.map((c) => c.level);

  // Next level or custom selected target level
  const effectiveTargetLevel =
    selectedTargetLevel ?? (rootNode.level < 8 ? ((rootNode.level + 1) as CareerLevel) : null);

  const careerGap = calculateCareerGap(
    rootNode.level,
    rootNode.totalCumulativeAE,
    rootNode.personalCumulativeAE,
    directBranchLevels,
    effectiveTargetLevel || undefined
  );

  const incomeProgressPercent =
    targetIncomeUAH > 0 ? Math.min(100, Math.round((currentIncomeUAH / targetIncomeUAH) * 100)) : 100;
  const isIncomeGoalAchieved = currentIncomeUAH >= targetIncomeUAH;
  const missingIncome = Math.max(0, targetIncomeUAH - currentIncomeUAH);

  return (
    <Card variant="elevated" className={`border-slate-700/80 overflow-hidden ${className}`}>
      <CardHeader className="bg-gradient-to-r from-slate-900 via-slate-800/80 to-slate-900 border-b border-white/10 pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent-emerald/15 border border-accent-emerald/30 flex items-center justify-center text-accent-emerald flex-shrink-0">
              <Target className="w-5 h-5" />
            </div>
            <div>
              <CardTitle className="text-base sm:text-lg">Трекер фінансових та кар'єрних цілей</CardTitle>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span
              className={`text-xs px-2.5 py-1 rounded-lg font-mono font-semibold border ${
                isIncomeGoalAchieved
                  ? 'bg-emerald-500/20 text-accent-neon border-emerald-500/40 shadow-[0_0_10px_rgba(0,229,153,0.3)]'
                  : 'bg-slate-800 text-cyan-300 border-white/10'
              }`}
            >
              Ціль: {incomeProgressPercent}% досягнуто
            </span>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-3.5 sm:p-4 space-y-5">
        {/* SECTION 1: Target Income Slider & Presets */}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <label className="text-xs sm:text-sm font-semibold text-text-primary flex items-center gap-2 min-w-0">
              <TrendingUp className="w-4 h-4 text-accent-neon shrink-0" />
              <span className="leading-snug">Цільовий місячний дохід (грн/міс):</span>
            </label>
            <span className="text-sm sm:text-base font-bold font-mono text-accent-neon whitespace-nowrap ml-auto">
              {formatCurrencyUAH(targetIncomeUAH, 0)}
            </span>
          </div>

          {/* Quick Presets */}
          <div className="flex items-center gap-2 flex-wrap">
            {INCOME_PRESETS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                onClick={() => setTargetIncomeUAH(preset.value)}
                className={`
                  px-2.5 py-1 rounded-lg text-xs font-mono transition-all duration-150 cursor-pointer
                  border min-h-[32px]
                  ${
                    targetIncomeUAH === preset.value
                      ? 'bg-accent-emerald text-slate-950 font-bold border-accent-emerald shadow-[0_0_12px_rgba(16,185,129,0.4)]'
                      : 'bg-surface-hover/70 text-text-secondary hover:text-text-primary border-white/10 hover:border-white/20'
                  }
                `}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Slider */}
          <Slider
            value={targetIncomeUAH}
            min={10_000}
            max={1_000_000}
            step={10_000}
            valueFormatter={(val) => formatCurrencyUAH(val, 0)}
            onChange={setTargetIncomeUAH}
          />

          {/* Income Progress Bar */}
          <div className="glass-card rounded-xl p-3.5 sm:p-4 border border-white/5 space-y-2">
            <div className="flex items-center justify-between text-xs gap-2 flex-wrap">
              <span className="text-text-secondary">
                Поточний дохід: <strong className="text-text-primary font-mono whitespace-nowrap">{formatCurrencyUAH(currentIncomeUAH, 0)}</strong>
              </span>
              <span className="text-text-secondary">
                {isIncomeGoalAchieved ? (
                  <span className="text-accent-neon font-semibold flex items-center gap-1 whitespace-nowrap">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Мету досягнуто!
                  </span>
                ) : (
                  <span className="whitespace-nowrap">
                    Залишилось:{' '}
                    <strong className="text-rose-300 font-mono whitespace-nowrap">+{formatCurrencyUAH(missingIncome, 0)}</strong>
                  </span>
                )}
              </span>
            </div>

            <div className="w-full h-3 rounded-full bg-slate-800 overflow-hidden border border-white/10">
              <div
                className="h-full bg-gradient-to-r from-accent-emerald via-accent-neon to-cyan-400 transition-all duration-300 shadow-[0_0_15px_rgba(0,229,153,0.5)]"
                style={{ width: `${incomeProgressPercent}%` }}
              />
            </div>
          </div>
        </div>

        {/* SECTION 2: Career Advancement Gap Engine */}
        <div className="space-y-4 pt-2 border-t border-white/10">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-xs sm:text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                <Award className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <span>
                  Критерії підвищення до Рівня {careerGap.nextLevel || rootNode.level} ({getLevelTitle(careerGap.nextLevel || rootNode.level)})
                </span>
              </h4>
            </div>

            {/* Target Level Switcher Buttons */}
            <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
              <span className="text-[11px] text-text-muted mr-1">Цільовий ранг:</span>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((lvl) => {
                const isCurrent = rootNode.level === lvl;
                const isSelected = effectiveTargetLevel === lvl;
                return (
                  <button
                    key={lvl}
                    type="button"
                    onClick={() => setSelectedTargetLevel(lvl as CareerLevel)}
                    className={`
                      px-2.5 py-1 rounded-lg text-xs font-mono transition-all cursor-pointer border min-w-[34px]
                      ${
                        isSelected
                          ? 'bg-amber-500 text-slate-950 font-bold border-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.4)]'
                          : isCurrent
                          ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                          : 'bg-slate-800 text-text-muted border-white/5 hover:text-text-primary'
                      }
                    `}
                  >
                    Р{lvl}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Criteria Breakdown Grid */}
          <div className="flex flex-col gap-2.5">
            {/* Condition 1: Total Cumulative Units */}
            <div className="glass-card rounded-xl p-3 border border-white/5 space-y-1.5">
              <div className="flex items-center justify-between text-xs gap-2">
                <span className="font-semibold text-text-primary flex items-center gap-1.5 truncate">
                  {careerGap.missingUnitsTotalAE === 0 ? (
                    <CheckCircle2 className="w-4 h-4 text-accent-emerald flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  )}
                  <span>1. Накопичений обіг структури</span>
                </span>
                <span className="font-mono text-xs text-accent-emerald font-bold whitespace-nowrap flex-shrink-0">
                  {careerGap.progressPercent}%
                </span>
              </div>

              <div className="w-full h-1.5 rounded-full bg-slate-800 overflow-hidden">
                <div
                  className="h-full bg-accent-emerald transition-all duration-300"
                  style={{ width: `${careerGap.progressPercent}%` }}
                />
              </div>

              <div className="flex items-center justify-between text-[11px] text-text-secondary gap-2">
                <span className="whitespace-nowrap">{formatAE(careerGap.currentUnitsTotalAE, 0)} накопичено</span>
                <span className="whitespace-nowrap">
                  {careerGap.missingUnitsTotalAE === 0 ? (
                    <span className="text-accent-emerald font-medium">Виконано ({formatAE(careerGap.targetThresholdAE, 0)})</span>
                  ) : (
                    <span className="text-amber-400 font-semibold">Бракує: {formatAE(careerGap.missingUnitsTotalAE, 0)}</span>
                  )}
                </span>
              </div>
            </div>

            {/* Condition 2: Personal Units (e.g. >= 300 for L3+) */}
            <div className="glass-card rounded-xl p-3 border border-white/5 space-y-1.5">
              <div className="flex items-center justify-between text-xs gap-2">
                <span className="font-semibold text-text-primary flex items-center gap-1.5 truncate">
                  {careerGap.isPersonalConditionMet ? (
                    <CheckCircle2 className="w-4 h-4 text-accent-emerald flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  )}
                  <span>2. Обов'язкові особисті одиниці</span>
                </span>
                <span className="font-mono text-xs text-cyan-400 font-bold whitespace-nowrap flex-shrink-0">
                  {formatAE(rootNode.personalCumulativeAE, 0)}
                </span>
              </div>

              <div className="text-[11px] text-text-secondary">
                {careerGap.missingPersonalAE === 0 ? (
                  <span className="text-accent-emerald font-medium">
                    Особисті договори в нормі (мінімум виконано)
                  </span>
                ) : (
                  <span className="text-rose-300 font-semibold">
                    Бракує {formatAE(careerGap.missingPersonalAE, 0)} особистих Е (вимога Р3+: від 300 Е)
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Condition 3: Branch Structure Configuration */}
          {careerGap.branchRequirements.length > 0 && (
            <div className="glass-card rounded-xl p-3.5 border border-white/5 space-y-2.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-text-primary flex items-center gap-1.5">
                  <GitBranch className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span>3. Необхідні прямі гілки</span>
                </span>
                <span className="text-xs font-mono flex-shrink-0">
                  {careerGap.isBranchConditionMet ? (
                    <Badge variant="emerald" size="sm">
                      Всі гілки кваліфіковані
                    </Badge>
                  ) : (
                    <Badge variant="amber" size="sm">
                      Потрібно розвинути гілки
                    </Badge>
                  )}
                </span>
              </div>

              <div className="flex flex-col gap-2 text-xs">
                {careerGap.branchRequirements.map((req) => (
                  <div
                    key={req.requiredLevel}
                    className={`
                      p-2.5 rounded-lg border flex items-center justify-between gap-3
                      ${
                        req.missingCount === 0
                          ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                          : 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                      }
                    `}
                  >
                    <div>
                      <div className="font-semibold text-xs">
                        Потрібно: {req.count} {req.count === 1 ? 'гілка' : 'гілки'} Р{req.requiredLevel}+
                      </div>
                      <div className="text-[11px] text-text-secondary mt-0.5">
                        {req.missingCount === 0 ? (
                          <span className="text-emerald-400 font-medium">✓ Усі наявні в структурі</span>
                        ) : (
                          <span className="text-rose-300 font-semibold">
                            Бракує ще {req.missingCount} {req.missingCount === 1 ? 'гілки' : 'гілок'}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="text-right font-mono font-bold text-xs flex-shrink-0">
                      {req.count - req.missingCount} / {req.count}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actionable Advice Pill */}
          <div className="bg-slate-900/90 border border-accent-emerald/20 rounded-xl p-3.5 flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-accent-neon flex-shrink-0 mt-0.5" />
            <div className="text-xs text-text-secondary space-y-1">
              <div className="text-text-primary font-semibold">
                Стратегічна рекомендація для закриття цілі:
              </div>
              <div>
                {careerGap.isFullyQualified ? (
                  <span className="text-accent-neon font-medium">
                    Усі умови для переходу на цей рівень виконано! Ранг присвоюється автоматично при завершенні звітного періоду.
                  </span>
                ) : (
                  <>
                    Фокусуйтесь на особистих зустрічах та підсиленні ключових лідерів direct-ліній.
                    {careerGap.missingUnitsTotalAE > 0 && ` Необхідно залучити ще ${formatAE(careerGap.missingUnitsTotalAE, 0)} структурного обігу.`}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
